import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import {
  TransferState,
  FundTransferRequest,
  TransferResult,
  TransferHistoryResponse,
  TransferResponse,
  TransferStatistics,
  FundDropdownValue,
  TransferHistoryQuery,
  ReceiverInfo,
} from "./types";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_WALLET_PERFIX,
]
  .filter(Boolean)
  .join("");

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_WALLET_PERFIX) {
  console.warn("Warning: REACT_APP_WALLET_PERFIX is not defined.");
}

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token from cookies
api.interceptors.request.use(
  async (config) => {
    const token = getCookie("access_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Async Thunks
export const getFundDropdownValues = createAsyncThunk(
  "transfer/getFundDropdownValues",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<{
        success: boolean;
        data: FundDropdownValue[];
      }>("/wallet/transfer/dropdown");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch fund dropdown values",
      );
    }
  },
);

export const transferFunds = createAsyncThunk(
  "transfer/transferFunds",
  async (data: FundTransferRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<{
        success: boolean;
        data: TransferResult;
      }>("/wallet/transfer", data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Fund transfer failed",
      );
    }
  },
);

export const getSentHistory = createAsyncThunk(
  "transfer/getSentHistory",
  async (query: TransferHistoryQuery, { rejectWithValue }) => {
    try {
      const response = await api.get<{
        success: boolean;
        data: TransferHistoryResponse;
      }>("/wallet/transfer/history/sent", { params: query });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch sent history",
      );
    }
  },
);

export const getReceivedHistory = createAsyncThunk(
  "transfer/getReceivedHistory",
  async (query: TransferHistoryQuery, { rejectWithValue }) => {
    try {
      const response = await api.get<{
        success: boolean;
        data: TransferHistoryResponse;
      }>("/wallet/transfer/history/received", { params: query });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch received history",
      );
    }
  },
);

export const getTransferDetails = createAsyncThunk(
  "transfer/getTransferDetails",
  async (transferNumber: string, { rejectWithValue }) => {
    try {
      const response = await api.get<{
        success: boolean;
        data: TransferResponse;
      }>(`/wallet/transfer/${transferNumber}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch transfer details",
      );
    }
  },
);

export const lookupReceiver = createAsyncThunk(
  "transfer/lookupReceiver",
  async (registerId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<{ success: boolean; data: ReceiverInfo }>(
        `/wallet/transfer/receiver/${registerId}`,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Customer not found",
      );
    }
  },
);

export const getTransferStatistics = createAsyncThunk(
  "transfer/getTransferStatistics",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<{
        success: boolean;
        data: TransferStatistics;
      }>("/wallet/transfer/statistics");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch transfer statistics",
      );
    }
  },
);

const initialState: TransferState = {
  dropdownValues: [],
  sentHistory: [],
  receivedHistory: [],
  pagination: null,
  statistics: null,
  currentTransfer: null,
  receiverInfo: null,
  receiverLoading: false,
  receiverError: null,
  loading: false,
  error: null,
  success: false,
};

const transferSlice = createSlice({
  name: "transfer",
  initialState,
  reducers: {
    clearTransferState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.receiverInfo = null;
      state.receiverLoading = false;
      state.receiverError = null;
    },
    resetCurrentTransfer: (state) => {
      state.currentTransfer = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Dropdown Values
      .addCase(getFundDropdownValues.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFundDropdownValues.fulfilled, (state, action) => {
        state.loading = false;
        state.dropdownValues = action.payload.data;
      })
      .addCase(getFundDropdownValues.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Transfer Funds
      .addCase(transferFunds.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(transferFunds.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(transferFunds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })
      // Sent History
      .addCase(getSentHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSentHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.sentHistory = action.payload.data.transfers;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(getSentHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Received History
      .addCase(getReceivedHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(getReceivedHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.receivedHistory = action.payload.data.transfers;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(getReceivedHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Transfer Details
      .addCase(getTransferDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTransferDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTransfer = action.payload.data;
      })
      .addCase(getTransferDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Statistics
      .addCase(getTransferStatistics.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTransferStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statistics = action.payload.data;
      })
      .addCase(getTransferStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Lookup Receiver
      .addCase(lookupReceiver.pending, (state) => {
        state.receiverLoading = true;
        state.receiverError = null;
        state.receiverInfo = null;
      })
      .addCase(lookupReceiver.fulfilled, (state, action) => {
        state.receiverLoading = false;
        state.receiverInfo = action.payload;
      })
      .addCase(lookupReceiver.rejected, (state, action) => {
        state.receiverLoading = false;
        state.receiverError = action.payload as string;
      });
  },
});

export const { clearTransferState, resetCurrentTransfer } =
  transferSlice.actions;
export default transferSlice.reducer;
